"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Check, Loader2, MessageCircleQuestion } from "lucide-react"
import type { User } from "@supabase/supabase-js"

import { Button } from "@/components/ui/button"
import { trackEvent, type EventProperties } from "@/lib/analytics"
import { errorMessageFrom, safeJson } from "@/lib/safe-json"
import type { DiagnosisResult } from "@/lib/types/diagnosis"

type ReviewReason = "inconclusive" | "disputed" | "high_risk"

interface DiagnosisReviewRequestProps {
  result: DiagnosisResult
  analysisId?: string | null
  user: User | null
  analyticsReady?: boolean
  reason: ReviewReason
  defaultOpen?: boolean
  /** "link" renders the closed trigger as plain text for hosts where the
   *  second look sits beneath a single primary action. */
  triggerStyle?: "button" | "link"
  funnelProperties: EventProperties
  /** Result-funnel notification, fired once per mount the first time the user
   *  commits to the review lane (opening the form, or submitting it when the
   *  host opened it already). Fires alongside — never instead of — this
   *  component's own `diagnosis_review_*` events. */
  onNextAction?: () => void
}

export function DiagnosisReviewRequest({
  result,
  analysisId,
  user,
  analyticsReady = true,
  reason,
  defaultOpen = false,
  triggerStyle = "button",
  funnelProperties,
  onNextAction,
}: DiagnosisReviewRequestProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [email, setEmail] = useState(user?.email ?? "")
  const [note, setNote] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [ticketNumber, setTicketNumber] = useState<string | null>(null)
  const exposureNodeRef = useRef<HTMLElement | null>(null)
  const nextActionFiredRef = useRef(false)
  const fireNextAction = () => {
    if (nextActionFiredRef.current) return
    nextActionFiredRef.current = true
    onNextAction?.()
  }
  const shownKeyRef = useRef<string | null>(null)
  const shownPropertiesRef = useRef({ funnelProperties, analysisId, reason, result })
  shownPropertiesRef.current = { funnelProperties, analysisId, reason, result }
  const setExposureNode = useCallback((node: HTMLElement | null) => {
    exposureNodeRef.current = node
  }, [])

  useEffect(() => {
    if (!email.trim() && user?.email) setEmail(user.email)
  }, [email, user?.email])

  useEffect(() => {
    if (!analyticsReady || typeof IntersectionObserver === 'undefined') return
    const node = exposureNodeRef.current
    if (!node) return
    const shownKey = `${funnelProperties.funnel_id ?? 'no-funnel'}:${funnelProperties.attempt_id ?? 'no-attempt'}:${analysisId ?? 'no-analysis'}:${reason}`
    if (shownKeyRef.current === shownKey) return

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || shownKeyRef.current === shownKey) return
      shownKeyRef.current = shownKey
      const latest = shownPropertiesRef.current
      trackEvent("diagnosis_review_cta_shown", {
        ...latest.funnelProperties,
        actual_destination: 'diagnosis_review_cta',
        analysis_id: latest.analysisId ?? undefined,
        reason: latest.reason,
        confidence: latest.result.confidence,
        problem_count: latest.result.problems.length,
      })
      observer.disconnect()
    }, { threshold: 0.35 })
    observer.observe(node)
    return () => observer.disconnect()
  }, [analysisId, analyticsReady, funnelProperties.attempt_id, funnelProperties.funnel_id, open, reason])

  useEffect(() => {
    if (defaultOpen) setOpen(true)
  }, [defaultOpen])

  const submit = async () => {
    fireNextAction()
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Enter the email where we should send the second look.")
      return
    }

    setSubmitting(true)
    setError("")
    try {
      const fullName = typeof user?.user_metadata?.full_name === "string"
        ? user.user_metadata.full_name.trim()
        : "Lawn owner"
      const resultUrl = analysisId
        ? `https://whatgrassisthis.com/result/${encodeURIComponent(analysisId)}`
        : undefined
      const response = await fetch("/api/contact/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName.length >= 2 ? fullName : "Lawn owner",
          email: email.trim(),
          subject: "Feedback",
          message: [
            `Please review this lawn diagnosis. Reason: ${reason.replace('_', ' ')}.`,
            note.trim() ? `User note: ${note.trim()}` : "User requested a second look from the result screen.",
          ].join("\n"),
          diagnosisReview: {
            source: 'web_diagnosis_result',
            analysisId: analysisId ?? undefined,
            resultUrl,
            confidence: result.confidence,
            region: result.region,
            evidencePhotoCount: result.imageUrls?.length || (result.image ? 1 : undefined),
            evidenceCoverImageIndex: result.evidenceCoverImageIndex,
            problems: result.problems,
          },
        }),
      })
      const data = (await safeJson<{ success?: boolean; ticket_number?: string; error?: unknown }>(response)) ?? {}
      if (!response.ok || !data.success) {
        throw new Error(errorMessageFrom(data.error, "We could not send the review request."))
      }
      const ticket = data.ticket_number ?? "received"
      setTicketNumber(ticket)
      trackEvent("diagnosis_review_submitted", {
        ...funnelProperties,
        actual_destination: 'support_ticket_receipt',
        outcome: 'success',
        analysis_id: analysisId ?? undefined,
        reason,
        confidence: result.confidence,
        problem_count: result.problems.length,
        ticket_number: ticket,
      })
    } catch (submitError) {
      trackEvent("diagnosis_review_submit_failed", {
        ...funnelProperties,
        actual_destination: 'diagnosis_review_form',
        outcome: 'failed',
        analysis_id: analysisId ?? undefined,
        reason,
        confidence: result.confidence,
        problem_count: result.problems.length,
        error_category: submitError instanceof Error ? submitError.message.slice(0, 120) : 'unknown',
      })
      setError(submitError instanceof Error ? submitError.message : "We could not send the review request.")
    } finally {
      setSubmitting(false)
    }
  }

  if (ticketNumber) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-green-100 bg-green-50 px-4 py-3">
        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-700" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-green-900">Second look requested</p>
          <p className="mt-0.5 text-xs leading-relaxed text-green-800">
            We will reply by email. Ticket {ticketNumber}.
          </p>
        </div>
      </div>
    )
  }

  if (!open) {
    return (
      <button
        ref={setExposureNode}
        type="button"
        onClick={() => {
          setOpen(true)
          fireNextAction()
          trackEvent("diagnosis_review_cta_tapped", {
            ...funnelProperties,
            actual_destination: 'diagnosis_review_form',
            analysis_id: analysisId ?? undefined,
            reason,
            confidence: result.confidence,
          })
        }}
        className={
          triggerStyle === "link"
            ? "inline-flex min-h-[44px] w-full items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-800 focus:outline-hidden focus:ring-2 focus:ring-green-500/30 rounded-xl"
            : "inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:border-green-200 hover:bg-green-50 hover:text-green-800 focus:outline-hidden focus:ring-2 focus:ring-green-500/30"
        }
      >
        <MessageCircleQuestion className="h-4 w-4" aria-hidden="true" />
        Ask for a second look
      </button>
    )
  }

  return (
    <div ref={setExposureNode} className="rounded-xl border border-gray-200 bg-white p-4 text-left">
      <p className="text-sm font-semibold text-gray-900">Ask for a second look</p>
      <p className="mt-1 text-xs leading-relaxed text-gray-600">
        We will review the photo and diagnosis, then reply by email. Do not apply a selective treatment while the result is uncertain.
      </p>
      <label className="mt-3 block text-xs font-semibold text-gray-700">
        Reply email
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          className="mt-1.5 min-h-[44px] w-full rounded-lg border border-gray-200 px-3 text-sm text-gray-900 outline-hidden focus:border-green-400 focus:ring-2 focus:ring-green-500/20"
        />
      </label>
      <label className="mt-3 block text-xs font-semibold text-gray-700">
        What looks wrong? <span className="font-normal text-gray-500">Optional</span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value.slice(0, 500))}
          rows={3}
          className="mt-1.5 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-hidden focus:border-green-400 focus:ring-2 focus:ring-green-500/20"
        />
      </label>
      {error && <p role="alert" className="mt-2 text-xs text-red-600">{error}</p>}
      <Button
        type="button"
        onClick={() => void submit()}
        disabled={submitting}
        className="mt-3 min-h-[44px] w-full bg-green-600 font-semibold text-white hover:bg-green-700"
      >
        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        Send for review
      </Button>
    </div>
  )
}
